import { useState, useRef, useEffect, memo, MouseEventHandler, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@ets/design-system';

import { useSelector, useIsNodeClamped } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';

import {
  selectReadOnlyMode,
  getDocumentObject,
  selectIsPageLayout,
} from 'Editor/redux/EditorStatusSlice';
import { selectCollaborators } from 'App/redux/appSlice';

import { UserPresentation } from '_common/components';

import PageLayoutTooltip from '../PageLayoutTooltip/PageLayoutTooltip';

import TrackedActionBody, { getContentValue, CONTENT_TEXT_LABELS } from './TrackedActionBody';

import styles from './TrackedActionCard.module.scss';
import { LikeToggle, Priority, ReplyList, ReplyToggle } from '_common/suite/components/Card';
import { Card } from '_common/suite/components';
import { LikeToggleProps } from '_common/suite/components/Card/LikeToggle/LikeToggle';
import { ReplyListProps } from '_common/suite/components/Card/ReplyList/ReplyList';

const NO_CONTENT_ACTIONS = [
  'ADD_PAGEBREAK',
  'ADD_TABLE',
  'ADD_IMAGE',
  'ADD_TABLE_CONTENTS',
  'ADD_LIST_FIGURES',
  'ADD_LIST_TABLES',
  'DELETE_FIGURE',
];

type PriorityProps = 'MEDIUM' | 'LOW' | 'HIGH';

type TrackedActionProps = {
  author: string;
  user?: string;
  comments: Editor.Data.Reply[];
  content:
    | {
        [key: string]: {
          type: string;
          value: string;
        };
      }
    | string;
  id: string;
  location?: string[];
  nodesPermissions?: {
    users: {
      [id: string]: string[];
    };
  };
  priority: PriorityProps;
  status: string;
  time: ISODate;
  type: Editor.TrackedAction['type'];
  votes: {
    user: string;
    value: number;
    time: string;
  }[];
  number?: number;
  deleted_replies?: string[];
  application?: string;
};

export type TrackedActionCardProps = {
  className?: string;
  selected?: boolean;
  panel?: boolean;
  trackedAction: TrackedActionProps;
  testId: string;
};

const TrackedActionCardContent = memo<TrackedActionCardProps>(
  ({ className, selected, panel = false, trackedAction, testId }) => {
    const intl = useIntl();

    const contentRef = useRef(null);

    const isPageLayout = useSelector(selectIsPageLayout);
    const isReadOnlyMode = useSelector(selectReadOnlyMode);
    const userId = useSelector((state) => state.auth.userId);
    const doc = useSelector(getDocumentObject);
    const collaborators = useSelector((state) => selectCollaborators(state, doc.id));

    const [readMore, setReadMore] = useState(true);
    const [showReplies, setShowReplies] = useState(false);

    const [contentValue, setContentValue] = useState<
      {
        type: string;
        value: string;
      }[]
    >([]);

    const isClamped = useIsNodeClamped({
      ref: contentRef,
      clamp: 3,
      dependencies: [
        trackedAction.content,
        typeof trackedAction?.content === 'object' && trackedAction?.content?.inserted?.value,
        typeof trackedAction?.content === 'object' && trackedAction?.content?.deleted?.value,
        contentValue,
      ],
    });

    const isAuthor = trackedAction.author === userId;
    const isOwner =
      doc.user_permissions.includes('owner') || doc.user_permissions.includes('admin');
    const isCommentator =
      doc.user_permissions.includes('comment') ||
      (trackedAction.nodesPermissions &&
        trackedAction.nodesPermissions.users[userId] &&
        trackedAction.nodesPermissions.users[userId].includes('comment'));
    const hasPermissions = (!isAuthor && isOwner) || (isAuthor && (isOwner || isCommentator));
    const canChangePriority = isAuthor || isOwner;
    // Reply to a suggestion: Suggestion Creator, Comment permissions, or user with owner permission
    const canReply = isAuthor || doc.user_permissions.includes('comment') || isOwner;
    const disabled = isReadOnlyMode || isPageLayout || !canChangePriority;

    //#region Data parsing
    const parseReply = (originalReply: Editor.Data.Reply): Card.Reply => {
      const reply: Card.Reply = {
        id: originalReply.id,
        authorId: originalReply.author,
        creationDate: originalReply.time,
        modificationDate: '',
        votes: originalReply.votes,
        content: { dir: 'ltr', content: originalReply.content.content },
      };

      return reply;
    };

    const parsedSuggestion = useMemo<Card.Suggestion>(
      () => ({
        id: trackedAction.id,
        replies: trackedAction.comments.map((reply) => parseReply(reply)),
      }),
      [trackedAction],
    );
    //#endregion

    useEffect(() => {
      if (!isClamped) {
        setReadMore(false);
      }
    }, [isClamped]);

    useEffect(() => {
      setShowReplies(trackedAction.comments?.length > 0);
    }, [trackedAction.id]);

    useEffect(() => {
      setContentValue(
        getContentValue({ content: trackedAction.content, type: trackedAction.type }),
      );
    }, [trackedAction.id, trackedAction.content]);

    const handleToggleReadMore: MouseEventHandler<HTMLDivElement> = (e) => {
      /**
       * Stop propagation to avoid card selection
       * Card selection will invoke scrollIntoView and app might pause to load new content
       */
      e.stopPropagation();

      setReadMore(!readMore);
    };

    const handleVoteTrackedAction: LikeToggleProps['onVote'] = ({ replyId, currentUserLiked }) => {
      if (replyId) {
        EditorManager.getInstance().voteTrackedActionReply(
          trackedAction.id,
          replyId,
          currentUserLiked ? false : true,
        );
      } else {
        EditorManager.getInstance().voteTrackedAction(
          trackedAction.id,
          currentUserLiked ? false : true,
        );
      }
    };

    const handleAcceptClicked = () => {
      EditorManager.getInstance().focusTrackedAction(trackedAction.id, () => {
        EditorManager.getInstance().acceptTrackedAction(trackedAction.id);
      });
    };

    const handleTrackedActionSelected = () => {
      if (!selected) {
        EditorManager.getInstance().focusTrackedAction(trackedAction.id);
      }
    };

    const handleRejectClicked = () => {
      EditorManager.getInstance().focusTrackedAction(trackedAction.id, () => {
        EditorManager.getInstance().rejectTrackedAction(trackedAction.id);
      });
    };

    const handleChangePriority = (priority: Card.Priority) => {
      if (trackedAction.status === 'OPEN') {
        let newPriotiy: typeof trackedAction.priority = 'MEDIUM';

        switch (priority) {
          case 'High': {
            newPriotiy = 'HIGH';
            break;
          }
          case 'Medium': {
            newPriotiy = 'MEDIUM';
            break;
          }
          case 'Low': {
            newPriotiy = 'LOW';
            break;
          }
          default: {
            break;
          }
        }
        if (trackedAction.priority !== newPriotiy) {
          EditorManager.getInstance().changeTrackedActionPriority(trackedAction.id, newPriotiy);
        }
      }
    };

    const handleToggleReplies = () => {
      setShowReplies(!showReplies);
    };

    //#region Handle replies
    const isReplyAuthor = (reply: Card.Reply) => reply.authorId === userId;

    const handleCreateReply: ReplyListProps['createReply'] = (reply) => {
      EditorManager.getInstance().replyTrackedAction(parsedSuggestion.id, reply);
    };

    const handleDeleteReply = (replyId: string) => {
      EditorManager.getInstance().deleteTrackedActionReply(parsedSuggestion.id, replyId);
    };

    const handleEditReply: ReplyListProps['editReply'] = ({ replyId, newContent }) => {
      EditorManager.getInstance().editTrackedActionReply(parsedSuggestion.id, replyId, newContent);
    };

    const handleVoteReply: LikeToggleProps['onVote'] = ({ replyId, currentUserLiked }) => {
      if (replyId && trackedAction.status !== 'd') {
        EditorManager.getInstance().voteTrackedActionReply(
          trackedAction.id,
          replyId,
          !currentUserLiked,
        );
      }
    };

    const canEditReply = (replyId: string) => {
      const reply = parsedSuggestion.replies?.find((reply) => reply.id === replyId);
      if (reply) {
        return isReplyAuthor(reply) && !(isReadOnlyMode || isPageLayout);
      }
      return false;
    };

    const canDeleteReply = (replyId: string) => {
      const reply = parsedSuggestion.replies?.find((reply) => reply.id === replyId);
      if (reply) {
        return (
          (isOwner || (isReplyAuthor(reply) && hasPermissions)) && !(isReadOnlyMode || isPageLayout)
        );
      }

      return false;
    };

    const canVoteReply = () => {
      return !(isReadOnlyMode || isPageLayout);
    };

    const priority = useMemo(() => {
      switch (trackedAction.priority) {
        case 'HIGH': {
          return 'High';
        }
        case 'MEDIUM': {
          return 'Medium';
        }
        case 'LOW': {
          return 'Low';
        }
      }
    }, [trackedAction]);
    //#region
    //#endregion
    const renderHeader = () => {
      return (
        <>
          <Card.Header.Left>
            <UserPresentation
              userId={trackedAction.author || 'IMPORTED_USER'}
              fixedName={trackedAction.user}
              creationDate={trackedAction.time}
            />
          </Card.Header.Left>
          <Card.Header.Right>
            <PageLayoutTooltip
              type="trackedAction"
              testId={`tracked-action-card-${trackedAction.id}-priority`}
            >
              <span>
                <Priority
                  priority={priority}
                  onChange={handleChangePriority}
                  testId={`tracked-action-card-${trackedAction.id}`}
                  disabled={disabled}
                  hasPermissions={canChangePriority}
                />
              </span>
            </PageLayoutTooltip>
          </Card.Header.Right>
        </>
      );
    };

    const renderReadMore = () => {
      return (
        <div className={styles.readMore} onClick={handleToggleReadMore}>
          <FormattedMessage
            id={readMore ? 'editor.comments.readLess' : 'editor.comments.readMore'}
          />
        </div>
      );
    };

    const renderBody = () => {
      if (NO_CONTENT_ACTIONS.includes(trackedAction.type)) {
        return (
          <>
            {trackedAction.application && (
              <div className={styles.imported}>
                <FormattedMessage id="IMPORTED_FROM_WORD_DOCUMENT" />
              </div>
            )}
            <div className={`${styles.text} ${styles.bold}`}>
              {trackedAction.type ? (
                <FormattedMessage id={CONTENT_TEXT_LABELS[trackedAction.type]} />
              ) : (
                <FormattedMessage id="global.change" />
              )}
            </div>
          </>
        );
      }

      return (
        <>
          {trackedAction.application && (
            <div className={styles.imported}>
              <FormattedMessage id="IMPORTED_FROM_WORD_DOCUMENT" />
            </div>
          )}
          <span
            ref={contentRef}
            style={{ lineHeight: '2rem' }}
            className={` ${!readMore && styles.readLess}`}
          >
            <TrackedActionBody
              type={trackedAction.type}
              content={contentValue}
              contentRef={contentRef}
            />
          </span>

          {isClamped && renderReadMore()}
        </>
      );
    };

    const renderFooter = () => {
      const isAuthor = userId === trackedAction.author;
      const isOwner =
        doc.user_permissions.includes('owner') || doc.user_permissions.includes('admin');

      // Accept a suggestion: User with owner permission.
      const canAccept = isOwner;
      // Reject a suggestion: Suggestion Creator or user with Owner permission
      const canReject = isAuthor || isOwner;

      return (
        <>
          <Card.Footer.Left>
            <LikeToggle
              votes={trackedAction.votes ?? []}
              target="comment"
              onVote={handleVoteTrackedAction}
              disabled={isReadOnlyMode || isPageLayout}
              testId={testId}
              legacyModal={true}
              renderWrapper={(children) => (
                <PageLayoutTooltip
                  type="trackedAction"
                  testId={`comment-card-${trackedAction.id}-like`}
                >
                  <span>{children}</span>
                </PageLayoutTooltip>
              )}
            />

            <PageLayoutTooltip
              type="trackedAction"
              disabled={!!parsedSuggestion.replies?.length}
              testId={`task-card-${parsedSuggestion.id}-reply`}
            >
              <ReplyToggle
                repliesCount={parsedSuggestion.replies?.length ?? 0}
                isToggled={showReplies}
                onToggleReplies={handleToggleReplies}
                canComment={canReply}
                id={`task-${parsedSuggestion.id}-replyToggle`}
                testId={testId}
              />
            </PageLayoutTooltip>
          </Card.Footer.Left>
          <PageLayoutTooltip
            type="trackedAction"
            visible={!isOwner}
            content={intl.formatMessage({ id: 'ACCEPT_CHANGE' })}
            testId={`tracked-action-card-${trackedAction.id}-accept-button`}
          >
            <Button
              variant="link"
              disabled={!canAccept || isReadOnlyMode || isPageLayout}
              size="s"
              onClick={handleAcceptClicked}
              testId={`tracked-action-card-${trackedAction.id}-accept`}
              style={{ margin: '0 0.5rem 0 0' }}
            >
              <FormattedMessage id="ACCEPT" />
            </Button>
          </PageLayoutTooltip>
          <PageLayoutTooltip
            type="trackedAction"
            visible={!isAuthor && !isOwner}
            content={intl.formatMessage({ id: 'REJECT_CHANGE' })}
            testId={`tracked-action-card-${trackedAction.id}-reject-button`}
          >
            <Button
              variant="link"
              disabled={!canReject || isReadOnlyMode || isPageLayout}
              size="s"
              onClick={handleRejectClicked}
              testId={`tracked-action-card-${trackedAction.id}-reject`}
            >
              <FormattedMessage id="REJECT" />
            </Button>
          </PageLayoutTooltip>
        </>
      );
    };

    return (
      <Card
        testId={`TrackedAction#${trackedAction.id}`}
        flat={panel}
        selected={selected}
        onClick={panel ? handleTrackedActionSelected : () => {}}
        width={panel ? '100%' : '43rem'}
      >
        <Card.Header size="medium">{renderHeader()}</Card.Header>
        <Card.Body>{renderBody()}</Card.Body>
        <Card.Footer size="large">{renderFooter()}</Card.Footer>
        {showReplies && (
          <ReplyList
            replies={parsedSuggestion.replies ?? []}
            testId={`${testId}-replyList`}
            commentId={parsedSuggestion.id}
            collaborators={collaborators}
            canComment={canReply}
            newReplyInteraction={{ environment: 'editor' }}
            optionsToggleTooltip={{
              content: intl.formatMessage({
                id: 'CANNOT_MAKE_CHANGES_TO_TRACK_CHANGES_IN_PAGE_LAYOUT',
              }),
              visible: isPageLayout,
              testId: `suggestion-card-${parsedSuggestion.id}-reply-options`,
            }}
            likeToggleTooltip={{
              content: intl.formatMessage({
                id: 'CANNOT_MAKE_CHANGES_TO_TRACK_CHANGES_IN_PAGE_LAYOUT',
              }),
              visible: isPageLayout,
              testId: `suggestion-card-${parsedSuggestion.id}-reply-like`,
            }}
            createReply={handleCreateReply}
            editReply={handleEditReply}
            deleteReply={handleDeleteReply}
            voteReply={handleVoteReply}
            canEditReply={canEditReply}
            canDeleteReply={canDeleteReply}
            canVoteReply={canVoteReply}
            legacyModal={true}
          />
        )}
      </Card>
    );
  },
);

export default TrackedActionCardContent;
